/**
 * A route test's results on the Gateways tab (user, 19/10/2026: "a gateway
 * overview for route tests"), one section per route test that opens like the
 * others: closed, its state and a one-line summary; open, the winner (gateway
 * + sender, where it was applied — 21/10/2026: and where the next one took
 * over), the gateways in the formula's order (the ranking), the per-sender
 * rows behind "Per sender", and what happens next.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import { AlertTriangle, FlaskConical, Pencil, Settings2 } from 'lucide-react';
import type { DropGenRouteTestSample, DropGenRouteTestToday } from '@trafficpulse/shared';

import { Badge } from '@/components/ui';

import { instantText } from '../../../utils/clock/dk-label';
import { useCountryClock } from '../../shared/time/country-clock-context';
import { Collapse } from '../../timeline/section/Collapse';
import { SectionAction } from '../../timeline/section/SectionAction';
import { SectionToggle } from '../../timeline/section/SectionToggle';
import { PerSender } from './PerSender';
import { RankingBlock } from './RankingBlock';
import { routeTestNext, routeTestProblem, routeTestSummary, STATE_WORDS } from './route-test-words';
import { WinnerBanner } from './WinnerBanner';

export const RouteTestSection: FC<{ test: DropGenRouteTestToday; plannedInPreview?: number; onEdit?: () => void; onEditMaster?: () => void }> = ({ test: read, plannedInPreview = 0, onEdit, onEditMaster }) => {
  // Before the day is generated the run has no rows: the plan preview says what it would lay.
  const test = read.drops.planned === 0 && plannedInPreview > 0 ? { ...read, drops: { ...read.drops, planned: plannedInPreview } } : read;
  const [open, setOpen] = useState(test.state !== 'waiting' || !test.followed);
  const clock = useCountryClock();
  const w = test.followed ? STATE_WORDS[test.state] : { badge: 'not followed', tone: 'warning' as const };
  const problem = routeTestProblem(test);
  const isWinner = (s: DropGenRouteTestSample) => test.winner !== null && s.routeId === test.winner.routeId && s.sender === test.winner.sender;
  return (
    <div className="border-t border-border/40" data-testid="route-test-section">
      <SectionToggle
        icon={FlaskConical}
        title={`Route test · ${test.masterName}`}
        badge={<Badge tone={w.tone}>{w.badge}</Badge>}
        summary={routeTestSummary(test)}
        open={open}
        onToggle={() => setOpen((v) => !v)}
        actions={
          <>
            {onEdit ? (
              <SectionAction icon={Pencil} onClick={onEdit} title="The route test's own setup: its drop slots, test gateways, texts, formula and rules">
                Edit route test
              </SectionAction>
            ) : null}
            {onEditMaster ? (
              <SectionAction icon={Settings2} primary={!test.followed} onClick={onEditMaster} title="The group's master config on its Gateways tab — where the strategy “Route tests” makes the winner apply">
                Master config
              </SectionAction>
            ) : null}
          </>
        }
      />
      <Collapse open={open}>
        {problem ? (
          <div className="mx-5 mb-2 flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-xs" data-testid="route-test-problem">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" aria-hidden />
            <span>{problem}</span>
          </div>
        ) : null}
        {test.winner ? <WinnerBanner winner={test.winner} /> : null}
        <RankingBlock test={test} />
        <PerSender samples={test.samples} isWinner={isWinner} />
        <p className="px-5 pb-3 pt-1 text-[11px] text-muted-foreground">
          {routeTestNext(test, test.readyAt ? instantText(test.readyAt, clock) : null)}
          {test.samples.some((s) => s.dropsWithStats < s.dropsSent) ? ' Rates cover the drops with stats; the rest are read at the next verdict pass.' : ''}
        </p>
      </Collapse>
    </div>
  );
};
